import "nprogress/nprogress.css";
import "../styles/globals.css";
import "intersection-observer";
import "@ungap/global-this";

import useCanGoBack from "@core/hooks/useCanGoBack";
import {
  FirstPageVisitedContext,
  FirstPageVisitedContextValue,
} from "@core/hooks/useFirstPageVisited";
import { IsAuthenticatingProvider } from "@core/hooks/useIsAuthenticating";
import AppShell from "@core/layout/AppShell";
import { initDatadogRum } from "@core/services/datadog-rum";
import type { Page } from "@core/types";
import { RouterScrollProvider } from "@moxy/next-router-scroll";
import { NextPage } from "next";
import type { AppProps as NextAppProps } from "next/app";
import { useRouter } from "next/router";
import type { Session } from "next-auth";
import { SessionProvider } from "next-auth/react";
import { DefaultSeo } from "next-seo";
import NProgress from "nprogress";
import { ReactElement, useEffect, useRef, useState } from "react";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "react-query";
// import { ReactQueryDevtools } from "react-query/devtools";
import { Hydrate } from "react-query/hydration";

NProgress.configure({ showSpinner: false });

interface PageProps {
  dehydratedState: unknown;
  session?: Session;
  err?: Error;
}

type AppProps = Omit<NextAppProps<PageProps>, "Component" | "pageProps"> & {
  Component: NextPage<PageProps> & {
    hideNavBar?: Page["hideNavBar"];
    hideNavBarOnMobile?: Page["hideNavBarOnMobile"];
    shouldShowConsentDialog?: Page["shouldShowConsentDialog"];
    backgroundColor?: Page["backgroundColor"];
    hideNavbarShadowOnMobile?: Page["hideNavbarShadowOnMobile"];
    hideNavbarShadowOnDesktop?: Page["hideNavbarShadowOnDesktop"];
  };
  pageProps: PageProps;
  err?: Error;
};

type SetTimeoutReturn = ReturnType<typeof setTimeout>;

const App = ({ Component, pageProps, err }: AppProps): ReactElement => {
  const router = useRouter();
  const { setCurrentPath } = useCanGoBack();

  // For attribution, we want to track the first page on the website that a
  // user visited.
  const [firstPageVisited] = useState<FirstPageVisitedContextValue | undefined>(
    typeof window !== "undefined" && typeof document !== "undefined"
      ? {
          page: window.location.href,
          referrer: document.referrer,
        }
      : undefined
  );

  // By default, when a user navigates to a page that is server-side rendered,
  // there is no indication that a page is loading. To improve UX, we use
  // a library called `nprogress` to display a small loading indicator at the
  // top of the page when a route changes.
  useEffect(() => {
    const delayInMilliseconds = 500;

    let timer: SetTimeoutReturn;

    const startProgressBar = () => {
      timer = setTimeout(() => {
        NProgress.start();
      }, delayInMilliseconds);
    };

    const finishProgressBar = () => {
      clearTimeout(timer);
      NProgress.done();
    };

    router.events.on("routeChangeStart", () => startProgressBar());
    router.events.on("routeChangeComplete", () => finishProgressBar());
    router.events.on("routeChangeError", () => finishProgressBar());

    return () => {
      router.events.off("routeChangeStart", startProgressBar);
      router.events.off("routeChangeComplete", finishProgressBar);
      router.events.off("routeChangeError", finishProgressBar);
    };
  }, [router.events]);

  useEffect(() => {
    setCurrentPath(router.asPath);
  }, [router.asPath, setCurrentPath]);

  useEffect(() => {
    initDatadogRum();
  }, []);

  const queryClientRef = useRef<QueryClient>();

  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient();
  }

  const {
    hideNavBar = false,
    hideNavBarOnMobile = false,
    backgroundColor,
    shouldShowConsentDialog = false,
    hideNavbarShadowOnMobile,
    hideNavbarShadowOnDesktop,
  } = Component;

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          success: {
            iconTheme: {
              primary: "#01959F",
              secondary: "#FFF",
            },
          },
          error: {
            iconTheme: {
              primary: "#E9503B",
              secondary: "#FFF",
            },
          },
          loading: {
            iconTheme: {
              primary: "#0065FF",
              secondary: "#FFF",
            },
          },
          blank: {
            iconTheme: {
              primary: "#0065FF",
              secondary: "#FFF",
            },
          },
        }}
      />

      <DefaultSeo
        dangerouslySetAllPagesToNoFollow={
          // Don't index staging
          process.env.NEXT_PUBLIC_ENVIRONMENT !== "prod" ||
          // Don't index iframe version
          process.env.NEXT_PUBLIC_WEB_APP_BASE_URL ===
            "https://app-iframe.treatmyocd.com" ||
          process.env.NEXT_PUBLIC_WEB_APP_BASE_URL ===
            "https://app-iframe-qa.treatmyocd.com"
        }
        dangerouslySetAllPagesToNoIndex={
          // Don't index staging
          process.env.NEXT_PUBLIC_ENVIRONMENT !== "prod" ||
          // Don't index iframe version
          process.env.NEXT_PUBLIC_WEB_APP_BASE_URL ===
            "https://app-iframe.treatmyocd.com" ||
          process.env.NEXT_PUBLIC_WEB_APP_BASE_URL ===
            "https://app-iframe-qa.treatmyocd.com"
        }
        title="Online OCD Treatment &amp; Therapy"
        titleTemplate="%s | NOCD"
        openGraph={{
          type: "website",
          locale: "en_US",
          site_name: "NOCD",
          url: process.env.NEXT_PUBLIC_CANONICAL_URL,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_CANONICAL_URL}/images/meta-image.png`,
            },
          ],
        }}
        twitter={{
          handle: "@treatmyocd",
          site: "@treatmyocd",
          cardType: "summary_large_image",
        }}
        additionalLinkTags={[
          {
            rel: "apple-touch-icon",
            sizes: "180x180",
            href: "/apple-touch-icon.png",
          },
          {
            rel: "icon",
            type: "image/png",
            sizes: "32x32",
            href: "/favicon-32x32.png",
          },
          {
            rel: "icon",
            type: "image/png",
            sizes: "16x16",
            href: "/favicon-16x16.png",
          },
          {
            rel: "manifest",
            href: "/site.webmanifest",
          },
          {
            rel: "mask-icon",
            href: "/safari-pinned-tab.svg",
            color: "#00a3ad",
          },
        ]}
        additionalMetaTags={[
          {
            name: "msapplication-TileColor",
            content: "#00a3ad",
          },
          {
            name: "theme-color",
            content: "#ffffff",
          },
          {
            name: "facebook-domain-verification",
            content: "jijnvn52abvys5k5tdztooul0ti55b",
          },
          {
            name: "viewport",
            content: "initial-scale=1.0, width=device-width",
          },
          // // Meta tag to associate the member portal with the NOCD app.
          // // If the user is on an iOS device, they'll be asked to launch
          // // or download the NOCD app.
          // hideNavBar
          //   ? null
          //   : { name: "apple-itunes-app", content: "app-id=1063365447" },
        ].filter(Boolean)}
      />
      <RouterScrollProvider>
        <SessionProvider session={pageProps.session}>
          <QueryClientProvider client={queryClientRef.current}>
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
            <Hydrate state={pageProps.dehydratedState}>
              <FirstPageVisitedContext.Provider value={firstPageVisited}>
                <IsAuthenticatingProvider>
                  <AppShell
                    hideNavBar={hideNavBar}
                    backgroundColor={backgroundColor}
                    shouldShowConsentDialog={shouldShowConsentDialog}
                    hideNavBarOnMobile={hideNavBarOnMobile}
                    hideNavbarShadowOnMobile={hideNavbarShadowOnMobile}
                    hideNavbarShadowOnDesktop={hideNavbarShadowOnDesktop}
                    {...pageProps}
                  >
                    <Component {...pageProps} err={err} />
                  </AppShell>
                </IsAuthenticatingProvider>
              </FirstPageVisitedContext.Provider>
            </Hydrate>
          </QueryClientProvider>
        </SessionProvider>
      </RouterScrollProvider>
    </>
  );
};

export default App;
